import React, { useEffect, useMemo, useState } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import {
  X,
  CaretRight,
  CaretLeft,
  CircleNotch,
  TreeStructure,
  UsersThree,
  Calendar,
  Envelope,
  Star,
  MagnifyingGlass,
  WarningCircle,
  Signature,
  PaperPlaneTilt,
  Eye,
  Code as CodeIcon,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import DateTimePicker from '@/components/ui/DateTimePicker'
import { StepIndicator } from '@/components/ui/StepIndicator'
import ApproverSelect from '@/components/approvals/ApproverSelect'
import { workflowsApi, api, senderEmailsApi } from '@/lib/api'
import { useScrollLock } from '@/lib/useScrollLock'
import { useCampaignStore } from '@/stores/campaignStore'
import { useAuthStore } from '@/stores/authStore'
import { useSignatureStore } from '@/stores/signatureStore'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { cn, getBadgeBorderClasses } from '@/lib/utils'
import { warnBannerClass, bannerTextStyle } from '@/lib/styleKit'
import { newlinesToBr } from '@/lib/signatureHtml'
import {
  browserTimeZone,
  defaultScheduledStart,
  effectiveStartIso,
  launchPreflightIssues,
  msToWallClock,
  startsImmediately,
  timeZoneChoices,
  wallClockToMs,
} from './launchPreflight'
import { describeSenderSelection, sameMailboxConflict, senderPayload, toggleSenderSelection } from './senderSelection'
import { warmupWarning } from './senderWarmupWarning'
import { audienceSize, dailyCapacity, pacing, pacingMessage } from './campaignPacing'
import {
  editSignature,
  initialSignatureStep,
  mailboxSaveNeeded,
  markMailboxSaved,
  markMineSaved,
  mineSaveNeeded,
  selectSignatureSource,
  signaturePayload,
  signatureStepBlocker,
  type SignatureSource,
  type SignatureStepState,
} from './signatureStep'
import type { CreateCampaignData } from '@/types/campaign'
import type { SenderEmail } from '@/types/senderEmail'

interface LaunchCampaignModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface WorkflowOption {
  id: number
  name: string
  status: string
  trigger_type?: string | null
}

interface ContactList {
  id: number
  name: string
  total_contacts: number
}

// Step labels, rendered by the shared §30 step indicator. Order is fixed by
// the approvals spec (5.5): Workflow, Sender, Signature, Audience, Schedule.
type Step = 1 | 2 | 3 | 4 | 5
const STEPS: { key: Step; label: string }[] = [
  { key: 1, label: 'Workflow' },
  { key: 2, label: 'Sender' },
  { key: 3, label: 'Signature' },
  { key: 4, label: 'Audience' },
  { key: 5, label: 'Schedule' },
]

// A Representative's campaign goes to an approver, and may carry a workflow
// that is not active yet; the approver activates it with the campaign.
const REP_SUBMITTABLE_WORKFLOW_STATUSES = ['draft', 'rejected', 'pending_approval']

// The source Select needs string values.
const MAILBOX_SOURCE = 'mailbox'
const sourceValue = (s: SignatureSource) => (s.kind === 'mailbox' ? MAILBOX_SOURCE : 'mine')
const parseSource = (v: string): SignatureSource => (v === MAILBOX_SOURCE ? { kind: 'mailbox' } : { kind: 'mine' })

const signatureTextareaClass =
  'w-full rounded-lg border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] bg-background dark:bg-white/[0.02] text-[13px] font-mono text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] px-3 py-2.5 leading-relaxed focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors resize-none overflow-y-auto'

// Selection-row chrome — replaces ring-1 (which gets clipped by parent
// overflow-y:auto on scroll containers; see §13 / §27 border-only standard).
const rowBase =
  'w-full text-left flex items-center gap-3 p-3.5 rounded-lg border transition-colors duration-150'
const rowIdle =
  'border-border/60 bg-card hover:bg-muted/30 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]'
const rowActive =
  'border-[#5e6ad2] bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.10] dark:border-[#7170ff]/70'

const LaunchCampaignModal: React.FC<LaunchCampaignModalProps> = ({ isOpen, onClose, onSuccess }) => {
  useScrollLock(isOpen)
  const { createCampaign } = useCampaignStore()
  const role = useAuthStore(s => s.user?.role)
  const isRep = role === 'representative'
  const mySignature = useSignatureStore(s => s.mine)
  const signaturesLoaded = useSignatureStore(s => s.loaded)
  const signaturesPlanLocked = useSignatureStore(s => s.planLocked)
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  const fetchMine = useSignatureStore(s => s.fetchMine)
  const saveMine = useSignatureStore(s => s.saveMine)
  const updateSenderEmail = useSenderEmailStore(s => s.updateSenderEmail)

  const [step, setStep] = useState<Step>(1)

  const [workflows, setWorkflows] = useState<WorkflowOption[]>([])
  const [contactLists, setContactLists] = useState<ContactList[]>([])
  const [verifiedSenders, setVerifiedSenders] = useState<SenderEmail[]>([])
  const [loadingWorkflows, setLoadingWorkflows] = useState(false)
  const [loadingLists, setLoadingLists] = useState(false)
  const [loadingSenders, setLoadingSenders] = useState(false)

  const [workflowQuery, setWorkflowQuery] = useState('')
  const [listQuery, setListQuery] = useState('')
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null)
  // Ordered: the first pick is the campaign's primary sender. Two or more
  // means the campaign rotates contacts across them (rotation_email.md).
  const [selectedSenderEmailIds, setSelectedSenderEmailIds] = useState<number[]>([])
  const [audienceType, setAudienceType] = useState<'list' | 'all'>('all')
  const [selectedListIds, setSelectedListIds] = useState<number[]>([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [scheduledStart, setScheduledStart] = useState('')
  // Zone the scheduled start is read in. Defaults to the browser zone.
  const [startTz, setStartTz] = useState<string>(() => browserTimeZone())
  const tzOptions = useMemo(() => timeZoneChoices([browserTimeZone(), startTz]), [startTz])
  // Changing the zone keeps the same instant and re-expresses it in the new zone.
  const changeStartTz = (next: string) => {
    if (scheduledStart) {
      const ms = wallClockToMs(scheduledStart, startTz)
      if (!Number.isNaN(ms)) setScheduledStart(msToWallClock(ms, next))
    }
    setStartTz(next)
  }
  // Step 3. Keyed off the primary sender (first pick on step 2); reset when
  // that changes, so the mailbox text never belongs to a different inbox.
  const [sigState, setSigState] = useState<SignatureStepState>(() => initialSignatureStep(undefined))
  const [sigMode, setSigMode] = useState<'edit' | 'preview'>('edit')
  const [savingSignature, setSavingSignature] = useState(false)
  // Step 5, Representatives only: who reviews this campaign.
  const [approverId, setApproverId] = useState<number | null>(null)

  const [isCreating, setIsCreating] = useState(false)

  // Only active workflows can power a campaign (enforced by the backend on
  // create and launch), except for a Representative, whose campaign goes to an
  // approver anyway: their own not-yet-active workflows are approved with it.
  const activeWorkflows = workflows.filter(
    wf => wf.status === 'active' || (isRep && REP_SUBMITTABLE_WORKFLOW_STATUSES.includes(wf.status)),
  )

  // Client-side, case-insensitive search by name over the active workflows.
  const visibleWorkflows = activeWorkflows.filter(wf =>
    wf.name.toLowerCase().includes(workflowQuery.trim().toLowerCase()),
  )

  // Same client-side name search over contact lists for the audience step.
  const visibleLists = contactLists.filter(list =>
    list.name.toLowerCase().includes(listQuery.trim().toLowerCase()),
  )

  const selectedWorkflow = workflows.find(w => w.id === selectedWorkflowId)
  const isContactAddedWorkflow = selectedWorkflow?.trigger_type === 'contact_added'

  const primarySenderId = selectedSenderEmailIds[0] ?? null
  const primarySender = verifiedSenders.find(s => s.id === primarySenderId)
  const sigBlocker = signatureStepBlocker(sigState)
  const sigSaveNeeded = mailboxSaveNeeded(sigState) || mineSaveNeeded(sigState)
  // Once it has loaded and the plan allows it, there is exactly ONE
  // signature -- the user's own -- regardless of which or how many
  // mailboxes this campaign sends from; no per-mailbox choice is offered.
  // On Starter the step is the mailbox signature alone (unchanged).
  const hasMySignature = signaturesLoaded && !signaturesPlanLocked

  // Fetch ALL workflows on open. The list endpoint is paginated (default 50,
  // max 200 per request), so we page through until a short page comes back.
  // Loading every workflow up front is what lets the client-side search below
  // match workflows beyond the first page (previously they were never loaded).
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const PAGE = 200
    setLoadingWorkflows(true)
    ;(async () => {
      try {
        const all: WorkflowOption[] = []
        for (let skip = 0; ; skip += PAGE) {
          const res = await workflowsApi.getAll(skip, PAGE)
          const page: WorkflowOption[] = res.data
          all.push(...page)
          if (page.length < PAGE) break
        }
        if (!cancelled) setWorkflows(all)
      } catch {
        // leave whatever was already loaded
      } finally {
        if (!cancelled) setLoadingWorkflows(false)
      }
    })()
    return () => { cancelled = true }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && step === 2 && verifiedSenders.length === 0) {
      setLoadingSenders(true)
      senderEmailsApi.getVerified()
        .then(res => {
          // Nothing is pre-selected (AB#22456). The account's default sender
          // used to be ticked on load, which made the step lie in two ways:
          // a mailbox the user never chose was already in the campaign, and
          // picking the one they actually wanted left BOTH selected, so the
          // deliverability warning named an inbox they had not asked for.
          //
          // The original reason for pre-selecting -- "so a user who never
          // touches this step still has a valid sender" -- does not hold: the
          // Next button on this step is disabled while nothing is selected,
          // and the pre-flight lists the missing sender as a blocker, so the
          // step cannot be skipped.
          setVerifiedSenders(res.data)
        })
        .catch(() => {})
        .finally(() => setLoadingSenders(false))
    }
  }, [isOpen, step])

  useEffect(() => {
    if (isOpen && step === 4 && contactLists.length === 0) {
      setLoadingLists(true)
      api.get('/contacts/lists')
        .then(res => setContactLists(res.data))
        .catch(() => {})
        .finally(() => setLoadingLists(false))
    }
  }, [isOpen, step])

  // The user's own signature is fetched once, on entering the signature
  // step. A plan gate (403) leaves the store `planLocked` and the step
  // offers the mailbox signature only; nothing here can fail the step.
  useEffect(() => {
    if (isOpen && step === 3 && !signaturesLoaded) {
      void fetchMine()
    }
  }, [isOpen, step, signaturesLoaded, fetchMine])

  // Start the signature step from the primary sender's own signature whenever
  // the primary changes (and when the senders list arrives after a pick) --
  // unless the account has per-user signatures, in which case there is only
  // ever one signature and it is always the user's own, regardless of which
  // mailbox(es) are picked.
  useEffect(() => {
    const base = initialSignatureStep(primarySender)
    setSigState(hasMySignature ? selectSignatureSource(base, { kind: 'mine' }, mySignature) : base)
    setSigMode('edit')
  }, [primarySenderId, primarySender?.signature_html, hasMySignature, mySignature])

  useEffect(() => {
    if (selectedWorkflowId) {
      const wf = workflows.find(w => w.id === selectedWorkflowId)
      if (wf) setName(`${wf.name} Campaign`)
    }
  }, [selectedWorkflowId, workflows])

  // A 'Contact Added to List' workflow only supports list-based audiences.
  // It needs specific list(s) to watch. Force that mode when such a workflow
  // is selected, and clear any 'all contacts' choice made before switching.
  useEffect(() => {
    if (isContactAddedWorkflow) {
      setAudienceType('list')
    }
  }, [isContactAddedWorkflow])

  useEffect(() => {
    if (isOpen && !scheduledStart) {
      // Local wall-clock value for <input type="datetime-local">, nudged into
      // the future so the wizard doesn't open already failing pre-flight.
      setScheduledStart(defaultScheduledStart(new Date(), undefined, startTz))
    }
  }, [isOpen])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      setStep(1)
      setWorkflowQuery('')
      setListQuery('')
      setSelectedWorkflowId(null)
      setSelectedSenderEmailIds([])
      setAudienceType('all')
      setSelectedListIds([])
      setName('')
      setDescription('')
      setScheduledStart('')
      setContactLists([])
      setVerifiedSenders([])
      setSigState(initialSignatureStep(undefined))
      setSigMode('edit')
      setSavingSignature(false)
      setApproverId(null)
      setIsCreating(false)
    }
  }, [isOpen])

  // Pre-flight (H3) — every blocker, surfaced on the review step instead of
  // being swallowed by the old silent `return` in handleCreate.
  const preflightIssues = launchPreflightIssues({
    selectedWorkflowId,
    selectedSenderEmailIds,
    verifiedSenderCount: verifiedSenders.length,
    audienceType,
    selectedListIds,
    name,
    scheduledStart,
    signatureConfirmed: sigBlocker === null,
    needsApprover: isRep,
    approverId,
  })
  const canSchedule = preflightIssues.length === 0

  /**
   * Mailbox mode with an edit: write it to the primary inbox so what the
   * wizard showed is what the executor appends. Returns false when the save
   * failed (the store has already toasted), so the caller stays on the step.
   */
  const saveSignatureToMailbox = async (): Promise<boolean> => {
    if (!primarySenderId) return false
    setSavingSignature(true)
    try {
      await updateSenderEmail(primarySenderId, { signature_html: sigState.html })
      const savedHtml = sigState.html
      setSigState(prev => markMailboxSaved(prev))
      // The wizard reads senders from its own list, not the store; patch it
      // so re-selecting this inbox shows what the mailbox now carries.
      setVerifiedSenders(prev => prev.map(s => (s.id === primarySenderId ? { ...s, signature_html: savedHtml } : s)))
      return true
    } catch {
      return false
    } finally {
      setSavingSignature(false)
    }
  }

  /**
   * An edit to the user's own signature: persist it as their permanent
   * signature (same record Settings > Signatures reads/writes), not just a
   * one-off snapshot -- entering it here IS entering it in Settings.
   */
  const saveSignatureAsMine = async (): Promise<boolean> => {
    setSavingSignature(true)
    try {
      await saveMine(sigState.html.trim())
      setSigState(prev => markMineSaved(prev))
      return true
    } catch {
      return false
    } finally {
      setSavingSignature(false)
    }
  }

  const handleNext = async () => {
    if (step === 3 && sigSaveNeeded) {
      const ok = mailboxSaveNeeded(sigState) ? await saveSignatureToMailbox() : await saveSignatureAsMine()
      if (!ok) return
    }
    setStep((step + 1) as Step)
  }

  const handleCreate = async () => {
    if (!selectedWorkflowId || !canSchedule) return
    setIsCreating(true)
    try {
      const data: CreateCampaignData = {
        name: name.trim(),
        workflow_id: selectedWorkflowId,
        // max(now, chosen): a pre-filled "now" goes stale while the wizard is
        // filled in, and a past time means "start now" rather than a moment
        // that has gone. Never moves a start earlier than the user asked.
        scheduled_start: effectiveStartIso(scheduledStart, Date.now(), startTz),
      }
      // Sent with the create, not PUT straight after it. The follow-up update
      // failed whenever the new campaign did not stay 'scheduled' — an
      // already-due start auto-launches to 'running', and a Representative's
      // campaign parks for approval — and the user lost the text they typed.
      const desc = description.trim()
      if (desc) data.description = desc
      Object.assign(data, senderPayload(selectedSenderEmailIds))
      if (audienceType === 'list' && selectedListIds.length > 0) data.contact_list_ids = selectedListIds
      else if (audienceType === 'all') data.target_all_contacts = true
      // Per-user signature: a snapshot of the confirmed text, so a later
      // change to the user's saved signature never alters this campaign.
      // Mailbox mode sends nothing, each inbox keeps using its own.
      Object.assign(data, signaturePayload(sigState))
      if (isRep && approverId) data.approver_id = approverId

      await createCampaign(data)
      onSuccess()
    } catch {
      // store handles toast
    } finally {
      setIsCreating(false)
    }
  }

  const senderSummary = describeSenderSelection(selectedSenderEmailIds, verifiedSenders)
  // Advisory, never a blocker: a spread campaign pushes an unwarmed mailbox to
  // its full cap from the first send (see senderWarmupWarning).
  const warmupCaution = warmupWarning(selectedSenderEmailIds, verifiedSenders)
  // How much of the chosen audience goes out on day one. Shown on the audience
  // step, where both halves of the answer are finally known.
  const pacingNote = pacingMessage(pacing(
    dailyCapacity(selectedSenderEmailIds, verifiedSenders),
    audienceSize(audienceType, selectedListIds, contactLists),
  ))
  const selectedListNames = contactLists
    .filter(l => selectedListIds.includes(l.id))
    .map(l => l.name)

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          {/* Backdrop — blur-only per §45 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed top-0 left-0 w-screen h-screen"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Card — §29 portal modal pattern */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="launch-campaign-title"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'relative z-10 w-full max-w-xl rounded-2xl border shadow-2xl flex flex-col max-h-[88vh] overflow-hidden',
              'bg-card border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08]',
            )}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/50 dark:border-white/[0.06]">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 border border-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30"
                  aria-hidden="true"
                >
                  <Calendar className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                </div>
                <div className="min-w-0">
                  {/* Labelled "Launch Campaign" per product decision, even though
                      this wizard still only *schedules* — its submit produces
                      status `scheduled`, not an immediate send. Starting the send
                      is "Launch now" on the campaign row, and restarting a paused
                      one is "Resume"; those two remain distinct actions. */}
                  <h2
                    id="launch-campaign-title"
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                  >
                    Launch Campaign
                  </h2>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    Step {step} of 5 · {STEPS[step - 1].label}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Step Indicator — §30 */}
            <div className="flex-shrink-0 px-6 pt-4 pb-3 border-b border-border/50 dark:border-white/[0.06]">
              <StepIndicator steps={STEPS} current={step} />
            </div>

            {/* Content — `px-1` inside scroll container leaves room for selected-row border
                so it doesn't clip against overflow-y:auto (Image 9 root cause) */}
            <div className="flex-1 overflow-y-auto px-6 py-5 min-h-[280px]">
              {/* Step 1: Select Workflow */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2.5 mb-1">
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]"
                        aria-hidden="true"
                      >
                        <TreeStructure className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                      >
                        Select workflow
                      </h3>
                    </div>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] ml-[2.375rem]"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      {isRep
                        ? 'Choose the email workflow to use for this campaign. Workflows that are not active yet will be sent for approval with this campaign.'
                        : 'Choose the email workflow to use for this campaign. Only active workflows are shown.'}
                    </p>
                  </div>

                  {loadingWorkflows ? (
                    <div className="flex items-center justify-center py-12">
                      <CircleNotch className="h-6 w-6 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                    </div>
                  ) : workflows.length === 0 ? (
                    <EmptyState icon={<TreeStructure className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />} text="No workflows found. Create a workflow first." />
                  ) : activeWorkflows.length === 0 ? (
                    <EmptyState
                      icon={<TreeStructure className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />}
                      text={isRep
                        ? 'No workflows you can submit. Create a workflow first.'
                        : 'No active workflows. Activate a workflow to launch a campaign.'}
                    />
                  ) : (
                    <div className="space-y-3">
                      <Input
                        type="text"
                        value={workflowQuery}
                        onChange={e => setWorkflowQuery(e.target.value)}
                        placeholder="Search workflows..."
                        icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
                        iconPosition="left"
                        aria-label="Search workflows"
                      />
                      {visibleWorkflows.length === 0 ? (
                        <EmptyState icon={<MagnifyingGlass className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />} text="No workflows match your search." />
                      ) : (
                        <ScrollList>
                          {visibleWorkflows.map(wf => (
                            <button
                              key={wf.id}
                              type="button"
                              onClick={() => setSelectedWorkflowId(wf.id)}
                              aria-pressed={selectedWorkflowId === wf.id}
                              className={cn(rowBase, selectedWorkflowId === wf.id ? rowActive : rowIdle)}
                            >
                              <div className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 bg-muted/40 dark:bg-white/[0.04]">
                                <TreeStructure className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                              </div>
                              <span
                                className="flex-1 min-w-0 truncate text-foreground dark:text-[#f7f8f8]"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {wf.name}
                              </span>
                              <span
                                className={cn(
                                  'inline-flex items-center px-2 py-0.5 rounded-full border uppercase',
                                  // Tone from the one status authority (§60) —
                                  // this row used to carry its own fifth map.
                                  getBadgeBorderClasses(wf.status),
                                )}
                                style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                              >
                                {wf.status === 'active' ? wf.status : 'Needs approval'}
                              </span>
                            </button>
                          ))}
                        </ScrollList>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Select Sender Email */}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2.5 mb-1">
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]"
                        aria-hidden="true"
                      >
                        <Envelope className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                      >
                        Select sender emails
                      </h3>
                    </div>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] ml-[2.375rem]"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      {selectedSenderEmailIds.length > 1
                        ? `Contacts will be spread across these ${selectedSenderEmailIds.length} inboxes. Each contact stays on one inbox for its whole sequence.`
                        : 'Choose one or more verified email accounts to send from. Pick several to spread contacts across them.'}
                    </p>
                  </div>

                  {loadingSenders ? (
                    <div className="flex items-center justify-center py-12">
                      <CircleNotch className="h-6 w-6 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                    </div>
                  ) : verifiedSenders.length === 0 ? (
                    <EmptyState icon={<Envelope className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />} text="No verified sender emails found. Add one in Mailboxes." />
                  ) : (
                    <ScrollList>
                      {verifiedSenders.map(sender => {
                        const isSelected = selectedSenderEmailIds.includes(sender.id)
                        // Same address under another selected row: one
                        // mailbox, one daily limit, selectable once.
                        const conflict = isSelected ? null : sameMailboxConflict(sender, selectedSenderEmailIds, verifiedSenders)
                        return (
                        <button
                          key={sender.id}
                          type="button"
                          onClick={() => setSelectedSenderEmailIds(prev => toggleSenderSelection(prev, sender, verifiedSenders))}
                          aria-pressed={isSelected}
                          disabled={Boolean(conflict)}
                          title={conflict ? `Same mailbox as ${conflict.label}` : undefined}
                          className={cn(rowBase, isSelected ? rowActive : rowIdle, conflict && 'opacity-50 cursor-not-allowed')}
                        >
                          <div className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 bg-muted/40 dark:bg-white/[0.04]">
                            <Envelope className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-foreground dark:text-[#f7f8f8] truncate"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                            >
                              {sender.label}
                            </p>
                            <p
                              className="text-muted-foreground dark:text-[#8a8f98] truncate"
                              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                            >
                              {sender.email_address}
                              {conflict ? ` · same mailbox as ${conflict.label}` : ''}
                            </p>
                          </div>
                          {sender.is_default && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30"
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              <Star className="h-3 w-3" weight="fill" /> Default
                            </span>
                          )}
                        </button>
                        )
                      })}
                    </ScrollList>
                  )}

                  {warmupCaution && (
                    <div
                      className={cn(warnBannerClass, 'items-start gap-2')}
                      role="status"
                      aria-live="polite"
                    >
                      <WarningCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" weight="fill" />
                      <span style={bannerTextStyle}>{warmupCaution}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Validate your signature (spec 5.5, C7/C8) */}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2.5 mb-1">
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]"
                        aria-hidden="true"
                      >
                        <Signature className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                      >
                        Validate your signature
                      </h3>
                    </div>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] ml-[2.375rem]"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      Every email in this campaign goes out with this signature. Confirm it before continuing.
                    </p>
                    {hasMySignature && (
                      <p
                        className="text-muted-foreground/80 dark:text-[#62666d] ml-[2.375rem]"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                      >
                        A contact whose owner has saved their own signature gets that one automatically,
                        overriding what's shown here for that contact.
                      </p>
                    )}
                  </div>

                  {/* On Starter/Free there is exactly one source (the
                      mailbox's own) and nothing else to pick, so the control
                      still reads as a confirmation, not a choice. Once
                      per-user signatures are available there is only ever
                      one signature -- the user's own -- regardless of which
                      or how many mailboxes this campaign sends from, so no
                      source picker is shown at all. */}
                  {!hasMySignature && (
                    <div>
                      <label
                        htmlFor="launch-signature-source"
                        className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]"
                      >
                        Signature
                      </label>
                      <Select
                        id="launch-signature-source"
                        value={sourceValue(sigState.source)}
                        onChange={v => {
                          setSigState(prev => selectSignatureSource(prev, parseSource(v), mySignature))
                        }}
                        options={[
                          { value: MAILBOX_SOURCE, label: `Mailbox signature (${primarySender?.email_address ?? 'primary sender'})` },
                        ]}
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <div className="inline-flex rounded-md border border-border/60 dark:border-white/[0.08] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setSigMode('edit')}
                        aria-pressed={sigMode === 'edit'}
                        className={cn(
                          'px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors',
                          sigMode === 'edit'
                            ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]'
                            : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]',
                        )}
                      >
                        <CodeIcon className="w-3 h-3" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setSigMode('preview')}
                        aria-pressed={sigMode === 'preview'}
                        className={cn(
                          'px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors border-l border-border/60 dark:border-white/[0.08]',
                          sigMode === 'preview'
                            ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]'
                            : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]',
                        )}
                      >
                        <Eye className="w-3 h-3" /> Preview
                      </button>
                    </div>
                  </div>

                  {sigMode === 'edit' ? (
                    <textarea
                      value={sigState.html}
                      onChange={e => setSigState(prev => editSignature(prev, e.target.value))}
                      placeholder={'Jane Doe\nHead of Growth, Acme Inc.\njane@acme.com'}
                      rows={6}
                      spellCheck={false}
                      aria-label="Signature text"
                      className={signatureTextareaClass}
                    />
                  ) : (
                    <div
                      className="rounded-lg border border-dashed border-border/60 dark:border-white/[0.08] bg-background dark:bg-white/[0.02] p-3 min-h-[120px]"
                      data-testid="signature-preview"
                    >
                      {sigState.html.trim() ? (
                        <div
                          className="text-sm text-foreground dark:text-[#e2e4e7]"
                          style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.5 }}
                          dangerouslySetInnerHTML={{ __html: newlinesToBr(sigState.html) }}
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground/70 italic">No signature set.</p>
                      )}
                    </div>
                  )}

                  {sigBlocker && (
                    <p
                      className="text-destructive dark:text-red-400"
                      role="status"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {sigBlocker}
                    </p>
                  )}

                  {mailboxSaveNeeded(sigState) && (
                    <div className="flex items-center justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { void saveSignatureToMailbox() }}
                        disabled={savingSignature}
                        loading={savingSignature}
                      >
                        Save to mailbox
                      </Button>
                    </div>
                  )}

                  {mineSaveNeeded(sigState) && (
                    <div className="flex items-center justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { void saveSignatureAsMine() }}
                        disabled={savingSignature}
                        loading={savingSignature}
                      >
                        Save signature
                      </Button>
                    </div>
                  )}

                  {!hasMySignature && selectedSenderEmailIds.length > 1 && (
                    <p
                      className="text-muted-foreground/80 dark:text-[#62666d]"
                      style={{ fontSize: '11px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                    >
                      Rotating inboxes each use their own mailbox signature
                    </p>
                  )}
                </div>
              )}

              {/* Step 4: Select Audience */}
              {step === 4 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2.5 mb-1">
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]"
                        aria-hidden="true"
                      >
                        <UsersThree className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                      >
                        Select audience
                      </h3>
                    </div>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] ml-[2.375rem]"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    >
                      Choose who will receive this campaign.
                    </p>
                  </div>

                  {isContactAddedWorkflow && (
                    <div
                      className="rounded-md px-3 py-2.5 text-xs"
                      style={{ backgroundColor: 'var(--bg-tertiary, rgba(94,106,210,0.08))', color: 'var(--text-tertiary, #9ca3af)' }}
                    >
                      This workflow watches contact lists. Pick one or more below, and new contacts
                      added to any of them later will automatically run through this workflow too.
                    </div>
                  )}

                  {!isContactAddedWorkflow && (
                    <ScrollList compact>
                      <RadioRow
                        active={audienceType === 'all'}
                        onClick={() => { setAudienceType('all'); setSelectedListIds([]) }}
                        title="All contacts"
                        description="Send to every contact in your database"
                      />
                      <RadioRow
                        active={audienceType === 'list'}
                        onClick={() => setAudienceType('list')}
                        title="Use contact list(s)"
                        description="Send to one or more specific contact lists"
                      />
                    </ScrollList>
                  )}

                  {audienceType === 'list' && (
                    <div className="space-y-2">
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] uppercase"
                        style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        Contact list(s)
                      </p>
                      {loadingLists ? (
                        <div className="flex items-center justify-center py-6">
                          <CircleNotch className="h-5 w-5 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                        </div>
                      ) : contactLists.length === 0 ? (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] text-center py-4"
                          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                        >
                          No contact lists found.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          <Input
                            type="text"
                            value={listQuery}
                            onChange={e => setListQuery(e.target.value)}
                            placeholder="Search contact lists..."
                            icon={<MagnifyingGlass className="h-4 w-4" weight="regular" />}
                            iconPosition="left"
                            aria-label="Search contact lists"
                          />
                          {visibleLists.length === 0 ? (
                            <EmptyState icon={<MagnifyingGlass className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />} text="No contact lists match your search." />
                          ) : (
                        <ScrollList>
                          {visibleLists.map(list => {
                            const isSelected = selectedListIds.includes(list.id)
                            return (
                              <button
                                key={list.id}
                                type="button"
                                onClick={() =>
                                  setSelectedListIds(prev =>
                                    isSelected ? prev.filter(id => id !== list.id) : [...prev, list.id],
                                  )
                                }
                                aria-pressed={isSelected}
                                className={cn(rowBase, isSelected ? rowActive : rowIdle)}
                              >
                                <span
                                  className={cn(
                                    'h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                                    isSelected
                                      ? 'border-[#5e6ad2] bg-[#5e6ad2] dark:border-[#7170ff] dark:bg-[#7170ff]'
                                      : 'border-muted-foreground/40 dark:border-white/[0.18]',
                                  )}
                                  aria-hidden="true"
                                >
                                  {isSelected && <span className="h-1.5 w-1.5 rounded-sm bg-white" />}
                                </span>
                                <span
                                  className="flex-1 min-w-0 truncate text-foreground dark:text-[#f7f8f8]"
                                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                                >
                                  {list.name}
                                </span>
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] tabular-nums"
                                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                >
                                  {list.total_contacts} contacts
                                </span>
                              </button>
                            )
                          })}
                        </ScrollList>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {pacingNote && (
                    <div
                      className={cn(warnBannerClass, 'items-start gap-2')}
                      role="status"
                      aria-live="polite"
                    >
                      <WarningCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" weight="fill" />
                      <span style={bannerTextStyle}>{pacingNote}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Schedule & Launch */}
              {step === 5 && (
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center gap-2.5 mb-1">
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]"
                        aria-hidden="true"
                      >
                        <Calendar className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
                      >
                        Review &amp; schedule
                      </h3>
                    </div>
                  </div>

                  <Input
                    label="Campaign name"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter campaign name"
                  />

                  {/* Textarea — match shared Input chrome per §15/§42 */}
                  <div>
                    <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                      Description (optional)
                    </label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Describe this campaign…"
                      rows={3}
                      className={cn(
                        'w-full px-3 py-2.5 rounded-md border transition-colors duration-150 resize-none',
                        'bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50',
                        'hover:bg-background hover:border-border/80',
                        'focus:outline-none focus-visible:border-[#7170ff]/55',
                        'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d]',
                        'dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14]',
                        'dark:focus-visible:border-[#7170ff]/75',
                      )}
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.5 }}
                    />
                  </div>

                  <DateTimePicker
                    label="Scheduled start"
                    value={scheduledStart}
                    onChange={(v) => setScheduledStart(v)}
                  />
                  <div>
                    <label
                      htmlFor="launch-start-tz"
                      className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]"
                    >
                      Time zone
                    </label>
                    <Select
                      id="launch-start-tz"
                      value={startTz}
                      onChange={changeStartTz}
                      options={tzOptions}
                      className="w-full"
                    />
                  </div>

                  {/* A Representative's campaign is reviewed, not launched (C3). */}
                  {isRep && (
                    <div>
                      <label
                        htmlFor="launch-approver"
                        className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]"
                      >
                        Send to
                      </label>
                      <ApproverSelect id="launch-approver" value={approverId} onChange={setApproverId} />
                    </div>
                  )}

                  {/* Summary */}
                  <div
                    className={cn(
                      'rounded-lg border p-4 space-y-2.5',
                      'bg-muted/30 border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]',
                    )}
                  >
                    <h4
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                    >
                      Campaign summary
                    </h4>
                    <SummaryRow icon={<TreeStructure className="h-3.5 w-3.5" weight="regular" />} label="Workflow" value={selectedWorkflow?.name || '—'} />
                    <SummaryRow icon={<Envelope className="h-3.5 w-3.5" weight="regular" />} label={selectedSenderEmailIds.length > 1 ? 'Inboxes' : 'Sender'} value={senderSummary} />
                    <SummaryRow icon={<UsersThree className="h-3.5 w-3.5" weight="regular" />} label="Audience" value={audienceType === 'all' ? 'All contacts' : selectedListNames.length > 0 ? selectedListNames.join(', ') : '—'} />
                    <SummaryRow
                      icon={<Calendar className="h-3.5 w-3.5" weight="regular" />}
                      label="Scheduled"
                      // Says which of the two will happen, so clamping a past
                      // start to "now" is never a surprise.
                      value={
                        !scheduledStart
                          ? '—'
                          : startsImmediately(scheduledStart, Date.now(), startTz)
                            ? 'Starts immediately'
                            : `${format(new Date(scheduledStart), 'MMM d, yyyy, h:mm a')} (${startTz.replace(/_/g, ' ')})`
                      }
                    />
                    <p
                      className="text-muted-foreground/80 dark:text-[#62666d] pt-0.5"
                      style={{ fontSize: '11px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
                    >
                      {isRep
                        ? 'Sent to your approver for review. Nothing goes out until they validate it.'
                        : 'Saved with status Scheduled — sending starts at the time above. To send before then, use “Launch now” on the campaign row.'}
                    </p>
                  </div>

                  {/* Pre-flight — every blocker, before the user commits. */}
                  {!canSchedule && (
                    <div
                      id="launch-preflight"
                      className={cn(warnBannerClass, 'flex-col items-stretch gap-1.5')}
                      role="alert"
                      aria-live="polite"
                    >
                      <div className="flex items-center gap-2">
                        <WarningCircle className="h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                        <span style={{ ...bannerTextStyle, fontWeight: 590 }}>
                          {preflightIssues.length === 1
                            ? 'One thing to fix before scheduling'
                            : `${preflightIssues.length} things to fix before scheduling`}
                        </span>
                      </div>
                      <ul className="list-disc pl-8 space-y-0.5" style={bannerTextStyle}>
                        {preflightIssues.map(issue => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-3.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.015]">
              <div>
                {step > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStep((step - 1) as Step)}
                    icon={<CaretLeft className="h-4 w-4" weight="bold" />}
                  >
                    Back
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                {step < 5 ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => { void handleNext() }}
                    loading={step === 3 && savingSignature}
                    disabled={
                      (step === 1 && !selectedWorkflowId) ||
                      // A campaign with no sender is accepted by the API and
                      // then cannot send, so the sender step is a real gate.
                      (step === 2 && selectedSenderEmailIds.length === 0) ||
                      // Blank signature: every email would go out unsigned.
                      (step === 3 && (sigBlocker !== null || savingSignature)) ||
                      (step === 4 && audienceType === 'list' && selectedListIds.length === 0)
                    }
                  >
                    Next
                    <CaretRight className="h-4 w-4 ml-1" weight="bold" />
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCreate}
                    disabled={!canSchedule || isCreating || planLapsed}
                    title={lapsedTitle}
                    loading={isCreating}
                    icon={isRep
                      ? <PaperPlaneTilt className="h-4 w-4" weight="regular" />
                      : <Calendar className="h-4 w-4" weight="regular" />}
                    aria-describedby={canSchedule ? undefined : 'launch-preflight'}
                  >
                    {isRep ? 'Review & validate' : 'Launch campaign'}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

// -----------------------------------------------------------------------------
// Module-local helpers per §43

function ScrollList({ children, compact }: { children: React.ReactNode; compact?: boolean }) {
  // `p-1 -m-1` gives a 4px breathing buffer so the selected row's border
  // doesn't get clipped horizontally by `overflow-y: auto` (Image 9 fix).
  return (
    <div className={cn('overflow-y-auto p-1 -m-1 space-y-2', compact ? 'max-h-44' : 'max-h-60')}>
      {children}
    </div>
  )
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="text-center py-8">
      <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
        {icon}
      </div>
      <p
        className="text-muted-foreground dark:text-[#8a8f98]"
        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
      >
        {text}
      </p>
    </div>
  )
}

function RadioRow({
  active,
  onClick,
  title,
  description,
}: {
  active: boolean
  onClick: () => void
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(rowBase, active ? rowActive : rowIdle)}
    >
      <span
        className={cn(
          'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
          active
            ? 'border-[#5e6ad2] dark:border-[#7170ff]'
            : 'border-muted-foreground/40 dark:border-white/[0.18]',
        )}
        aria-hidden="true"
      >
        {active && (
          <span className="h-1.5 w-1.5 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff]" />
        )}
      </span>
      <div className="min-w-0 flex-1 text-left">
        <p
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {title}
        </p>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
        >
          {description}
        </p>
      </div>
    </button>
  )
}

function SummaryRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div
      className="flex items-start gap-2 text-muted-foreground dark:text-[#8a8f98]"
      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
    >
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <span>
        {label}:{' '}
        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>
          {value}
        </span>
      </span>
    </div>
  )
}

export default LaunchCampaignModal
